import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Field, TextArea, EmptyState, Modal, Alert, Label, Spinner } from "@/components/ui";
import { fmtDateTime } from "@/lib/helpers";
import { Mic, Square, PenLine, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = () => reject(r.error);
  r.onload = () => { const s = String(r.result || ""); const i = s.indexOf(","); resolve(i >= 0 ? s.slice(i + 1) : s); };
  r.readAsDataURL(blob);
});

const fmtTime = (ms) => { const s = Math.floor(ms / 1000); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

const GrantCheck = ({ on, onClick, label, testid }) => (
  <button onClick={onClick} data-testid={testid} className="w-full flex items-start gap-3 text-left mb-3">
    <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${on ? "bg-brand border-brand" : "border-line"}`}>{on && <Check className="w-4 h-4 text-white" />}</span>
    <span className="text-sm text-ink">{label}</span>
  </button>
);

export default function Scribe() {
  const [mode, setMode] = useState("record");
  const [title, setTitle] = useState("");
  const [transcript, setTranscript] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [visits, setVisits] = useState(null);
  const [ambientAuth, setAmbientAuth] = useState(null);
  const [showGrant, setShowGrant] = useState(false);

  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const startRef = useRef(0);

  const load = useCallback(async () => {
    const [v, s] = await Promise.all([api.listVisits(), api.consentStatus().catch(() => ({ granted: false }))]);
    setVisits(v); setAmbientAuth(s);
  }, []);
  useEffect(() => { load(); return () => tickRef.current && clearInterval(tickRef.current); }, [load]);

  const beginRecording = async () => {
    setErr(null); setInfo(null); setRecordedBlob(null); setElapsed(0);
    if (!navigator.mediaDevices?.getUserMedia) { setErr("Recording is not supported in this browser."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        setRecordedBlob(new Blob(chunksRef.current, { type: "audio/webm" }));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      startRef.current = Date.now();
      tickRef.current = setInterval(() => setElapsed(Date.now() - startRef.current), 250);
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Microphone permission denied. Enable it in your browser settings." : (e?.message || "Could not start recording"));
    }
  };

  const stopRecording = () => {
    try { mediaRef.current?.stop(); } catch { /* recorder already stopped */ }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecording(false);
  };

  const submitAudio = async () => {
    if (!recordedBlob) return;
    setErr(null); setInfo(null); setBusy(true);
    try {
      const audio_base64 = await blobToBase64(recordedBlob);
      const doc = await api.createVisitAudio({ title: title.trim() || "Visit note", audio_base64, extension: "webm", consent_confirmed: consent });
      setInfo("Visit transcribed & summarized. Audio was never stored.");
      setTitle(""); setRecordedBlob(null); setElapsed(0);
      if (doc?.transcript) setTranscript(doc.transcript);
      await load();
      toast.success("Visit summarized");
    } catch (e) { setErr(e?.message || "Failed to upload recording"); }
    finally { setBusy(false); }
  };

  const submitTyped = async () => {
    setErr(null); setInfo(null); setBusy(true);
    try {
      await api.createVisit({ title: title.trim(), transcript: transcript.trim(), consent_confirmed: consent });
      setTitle(""); setTranscript(""); setInfo("Visit saved.");
      await load();
      toast.success("Visit summarized");
    } catch (e) { setErr(e?.message || "Failed"); }
    finally { setBusy(false); }
  };

  if (!visits) return <Spinner />;
  const authed = !!ambientAuth?.granted;

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="AI" title="Visit scribe" />
      <p className="text-sm text-subtle -mt-3 mb-5 max-w-2xl">
        Record or type visit notes. Whisper transcribes and Gemini extracts diagnosis, medications, follow-ups, and family tasks.
        Audio is transcribed then discarded — only the transcript and summary are saved.
      </p>

      <button data-testid="consent-toggle" onClick={() => setConsent((v) => !v)} className="w-full text-left mb-5">
        <Card className={consent ? "bg-brand-tint border-brand" : ""}>
          <div className="flex items-center gap-3">
            <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${consent ? "bg-brand border-brand" : "border-line"}`}>
              {consent && <Check className="w-4 h-4 text-white" />}
            </span>
            <span className="text-[15px] text-ink" data-testid="consent-label">I confirm all parties present at this visit are aware they are being recorded.</span>
          </div>
        </Card>
      </button>

      <div className="flex gap-2 mb-4">
        {[["record", Mic, "Record"], ["type", PenLine, "Type"]].map(([m, Icon, lbl]) => (
          <button key={m} data-testid={`mode-${m}`} onClick={() => setMode(m)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border ${mode === m ? "bg-brand text-white border-brand" : "bg-brand-tint text-brand-dark border-line"}`}>
            <Icon className="w-4 h-4" /> {lbl}
          </button>
        ))}
      </div>

      <Field label="Visit title" data-testid="visit-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Cardiology follow-up · Dr. Patel" />

      {mode === "record" ? (
        <>
          {!authed && (
            <Card className="bg-mint-tint border-mint-dark mb-4" data-testid="scribe-ambient-gate">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-6 h-6 text-mint-dark shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-ink">{ambientAuth?.needs_reauth ? "Please review the updated HIPAA authorization" : "HIPAA authorization required to record"}</div>
                  <p className="text-sm text-subtle mt-1">Ambient recording is disabled until you sign the one-time HIPAA authorization. You can still type visit notes.</p>
                  <div className="flex gap-2 mt-3">
                    <Button data-testid="scribe-grant-consent" onClick={() => setShowGrant(true)}>{ambientAuth?.needs_reauth ? "Review & re-sign" : "Grant authorization"}</Button>
                    <Button variant="secondary" data-testid="scribe-switch-type" onClick={() => setMode("type")}>Type instead</Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card className="flex flex-col items-center py-10 mb-4">
            <Label>{recording ? "Recording…" : recordedBlob ? "Ready to transcribe" : "Tap to record"}</Label>
            <button
              data-testid={recording ? "stop-recording-button" : "start-recording-button"}
              onClick={recording ? stopRecording : beginRecording}
              disabled={!consent || busy || !authed}
              className={`mt-4 w-24 h-24 rounded-full flex items-center justify-center text-white transition-all disabled:opacity-40 ${recording ? "bg-danger kb-recording" : "bg-mint"}`}>
              {recording ? <Square className="w-9 h-9" /> : <Mic className="w-10 h-10" />}
            </button>
            <div className="font-display text-3xl text-ink mt-4" data-testid="rec-timer">{fmtTime(elapsed)}</div>
            {!consent && <p className="text-xs text-warn mt-2">Confirm consent above to enable recording.</p>}
            {recordedBlob && !recording && (
              <div className="mt-5 w-full max-w-xs">
                <Button data-testid="transcribe-audio-button" onClick={submitAudio} loading={busy} className="w-full">Transcribe & summarize</Button>
                <button data-testid="discard-recording-button" onClick={() => { setRecordedBlob(null); setElapsed(0); }} className="block mx-auto mt-3 text-sm text-subtle hover:text-ink">Discard and re-record</button>
              </div>
            )}
          </Card>
        </>
      ) : (
        <>
          <TextArea label="Visit notes / transcript" data-testid="visit-transcript-input" value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={8}
            placeholder="Dr. reviewed BP readings, adjusted lisinopril to 20mg, ordered lipid panel, follow-up in 3 months..." />
          <Button data-testid="submit-visit-button" onClick={submitTyped} loading={busy} disabled={!consent || !title.trim() || transcript.trim().length < 10}>Summarize with AI</Button>
        </>
      )}

      <Alert testid="scribe-error">{err}</Alert>
      <Alert tone="success" testid="scribe-info">{info}</Alert>
      {busy && <p className="text-sm text-subtle mt-2">Transcribing and summarizing with AI — this can take 15–30 seconds…</p>}

      <h2 className="font-display text-xl text-ink mt-8 mb-3">Recent visits</h2>
      {visits.length === 0 ? (
        <Card><EmptyState testid="scribe-empty" icon={Mic} title="No visits recorded yet"
          body="Record after your next appointment. KinBridge transcribes it and drafts diagnosis, meds, follow-ups and tasks." /></Card>
      ) : (
        <div className="space-y-3">
          {visits.map((v) => (
            <Card key={v.id}>
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-brand-dark" />
                <span className="font-semibold text-ink flex-1 truncate">{v.title}</span>
              </div>
              <div className="text-xs text-subtle mt-1">{fmtDateTime(v.created_at)} · {v.by_user_name}</div>
              {v.summary?.diagnosis && (
                <div className="mt-3 space-y-2 text-sm">
                  <div><Label>Diagnosis</Label><div className="text-ink">{v.summary.diagnosis}</div></div>
                  {v.summary.medications?.length > 0 && <div><Label>Medications</Label>{v.summary.medications.map((m, i) => <div key={`${v.id}-med-${i}`} className="text-ink">• {m}</div>)}</div>}
                  {v.summary.follow_ups?.length > 0 && <div><Label>Follow-ups</Label>{v.summary.follow_ups.map((m, i) => <div key={`${v.id}-fu-${i}`} className="text-ink">• {m}</div>)}</div>}
                  {v.summary.family_tasks?.length > 0 && <div><Label>Family tasks</Label>{v.summary.family_tasks.map((m, i) => <div key={`${v.id}-ft-${i}`} className="text-ink">• {m}</div>)}</div>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <GrantModal open={showGrant} onClose={() => setShowGrant(false)} onGranted={async () => { setShowGrant(false); await load(); toast.success("HIPAA authorization signed"); }} />
    </div>
  );
}

function GrantModal({ open, onClose, onGranted }) {
  const [disclosure, setDisclosure] = useState(null);
  const [name, setName] = useState("");
  const [age, setAge] = useState(false);
  const [authz, setAuthz] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (open) api.consentDisclosure().then(setDisclosure).catch(() => {}); }, [open]);

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      await api.consentGrant({ age_18_or_guardian: age, audio_recording_authorized: authz, signature_name: name.trim() });
      setName(""); setAge(false); setAuthz(false);
      onGranted();
    } catch (e) { setErr(e?.detail || e?.message || "Could not save authorization"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="HIPAA ambient audio authorization" testid="grant-modal">
      <div className="max-h-52 overflow-y-auto text-xs text-subtle whitespace-pre-line border border-line rounded-lg p-3 mb-4 bg-white">
        {disclosure?.text || "Loading disclosure…"}
      </div>
      <GrantCheck on={age} onClick={() => setAge((v) => !v)} testid="grant-age" label="I confirm I am 18 or older, or the legal guardian of the patient." />
      <GrantCheck on={authz} onClick={() => setAuthz((v) => !v)} testid="grant-authz" label="I explicitly authorize KinBridge Health to capture and process ambient audio during medical visits." />
      <Field label="Digital signature (type your full name)" data-testid="grant-signature" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" />
      <Alert testid="grant-error">{err}</Alert>
      <Button data-testid="grant-submit" onClick={submit} loading={busy} disabled={!age || !authz || !name.trim()} className="w-full">Sign authorization</Button>
    </Modal>
  );
}
